// Which parts of a lesson video a student really watched. Pure and SDK-free.
//
// A watch map is a string of "0"/"1", one char per BUCKET_SEC of video
// ("1" = played through). Stored on progress/{uid}_{lessonId}.watched, so a
// 1-hour clip costs 360 chars. Seeking never marks the skipped range.

export const BUCKET_SEC = 10;
/** Share of the clip a student must really play for auto-complete. */
export const COMPLETE_RATIO = 0.8;
/** A jump bigger than this between two player ticks is a seek, not playback. */
const MAX_TICK_GAP_SEC = 3;

const bucketCount = (durationSec: number) => Math.max(1, Math.ceil(durationSec / BUCKET_SEC));

/** Mark the stretch played between two ticks (fromSec -> toSec). Non-mutating. */
export function markWatched(map: string, fromSec: number, toSec: number, durationSec: number): string {
  const gap = toSec - fromSec;
  if (gap < 0 || gap > MAX_TICK_GAP_SEC) return map; // seek or rewind jump
  const len = Math.max(map.length, bucketCount(durationSec));
  const chars = map.padEnd(len, "0").split("");
  const last = Math.min(len - 1, Math.floor(toSec / BUCKET_SEC));
  for (let i = Math.floor(fromSec / BUCKET_SEC); i <= last; i++) chars[i] = "1";
  return chars.join("");
}

/** 0..1 share of the clip that has been played. */
export function watchedRatio(map: string, durationSec: number): number {
  if (durationSec <= 0) return 0;
  const ones = map.split("").filter((c) => c === "1").length;
  return Math.min(1, ones / bucketCount(durationSec));
}

/** Per-bucket share of viewers (0..1) who watched it, across many students. */
export function heatmap(maps: string[]): number[] {
  const viewers = maps.filter((m) => m.includes("1"));
  const len = Math.max(0, ...viewers.map((m) => m.length));
  return Array.from({ length: len }, (_, i) =>
    viewers.length ? viewers.filter((m) => m[i] === "1").length / viewers.length : 0
  );
}

/** "1:05" from seconds. */
export function clock(sec: number): string {
  const s = Math.floor(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
